import React from "react"
import clsx from "clsx"
import { observer } from "mobx-react-lite"

import Icon from "@components/ui/Icon/Icon"
import SearchSummaryBlockStore from "@store/search/search-summary-block.store"
import { useSearchBlock } from "@pages/search/SearchContext/SearchResultContext"
import { useStore } from "@store"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/ui/Tooltip/v2/Tooltip"
import Text from "@components/ui/Typography/Text"
import {
  getDataSourceNodeContextProps,
  isDataSourceName,
} from "@framework/constants/upload"
import DocumentIcon from "@components/ui/Icon/DocumentIcon"

import styles from "./CitationBadge.module.sass"

interface Props {
  className?: string
  citationNumber: number
}

export const CitationBadge: React.FC<Props> = observer(
  ({ citationNumber, className }) => {
    const { searchEntityBlock } = useSearchBlock(SearchSummaryBlockStore)
    const { restrictionsStore: access } = useStore()

    const citation = searchEntityBlock.citationsStore?.getByIndex(
      citationNumber - 1
    )

    const isVerified = !!citation?.isExpertAnswer

    const isOld = !!citation?.isDocumentOld && access.showSearchContentWarming

    return (
      <span
        className={clsx(
          styles.root,
          {
            [styles.verified]: isVerified,
            [styles.contrast]: isVerified || isOld,
          },
          className
        )}
        data-index={citationNumber}
      >
        <Icon
          name={isVerified ? "shield-fill" : "checkbox-blank-circle-fill"}
          color={isOld ? "gold" : isVerified ? "green" : "primary"}
        />
      </span>
    )
  }
)

interface CitationDetailsProps {
  citationNumber: number
  citationId: string
}

export const CitationDetails: React.FC<CitationDetailsProps> = observer(
  ({ citationNumber, citationId, children }) => {
    const { searchEntityBlock } = useSearchBlock(SearchSummaryBlockStore)

    const citation = searchEntityBlock.citationsStore?.getByIndex(
      citationNumber - 1
    )

    const citationDetails =
      searchEntityBlock.citationsStore?.searchAnswersContextStore.getAnswerById(
        citationId
      )

    if (!citationDetails || !citation) return <>{children}</>

    const renderBadge = () => {
      if (citation?.isExpertAnswer) {
        return (
          <CitationBadge
            className={styles.citationBadge}
            citationNumber={citationNumber}
          />
        )
      }

      const sourceDescription =
        citation.contentSourceName &&
        isDataSourceName(citation.contentSourceName)
          ? getDataSourceNodeContextProps(citation.contentSourceName)
          : null

      return (
        <DocumentIcon size="small" icon={sourceDescription?.icon ?? "global"} />
      )
    }

    const renderTitle = () => {
      if (citation?.isExpertAnswer) {
        const title = citation.question || "N/A"
        return <span title={title}>Q: {title}</span>
      }

      const title =
        (citation.isExternalSource
          ? citation.sourceName || citation.sourceURL || "Web Citation"
          : citation.displayContentId) || "N/A"
      return <span title={title}>{title}</span>
    }

    return (
      <Tooltip color="primary" closeOnTargetPress>
        <TooltipTrigger>{children}</TooltipTrigger>
        <TooltipContent maxWidth="450px" className={styles.quotationContainer}>
          <div className={styles.header}>
            {renderBadge()}
            <Text variant="h5" color="text50Color" className={styles.title}>
              {renderTitle()}
            </Text>
          </div>
          <Text variant="body2" color="inherit">
            {citationDetails?.relevantText
              ? `"${citationDetails?.relevantText}"`
              : "Failed to load quotation"}
          </Text>
        </TooltipContent>
      </Tooltip>
    )
  }
)

export default CitationBadge
